"use client";

import { createContext, useContext, useMemo } from "react";

type ProfileTypes = {
  BUSINESS: any[];
  POI: any[];
  AMBASSADOR: any[];
  EVENT: any[];
  [key: string]: any[];
};

type AppData = {
  profileTypes: ProfileTypes;
  amenities: any[];
  pauAmenities: any[];
  vendorTypes: any[];
  serviceCategories: any[];
};

type AppDataDerived = AppData & {
  flattenedProfileTypes: any[];
  flattenedBusinessTypes: any[];
  flattenedEventTypes: any[];
  flattenedVendorTypes: any[];
  flattenedServiceCategories: any[];
};

const AppDataContext = createContext<AppDataDerived | null>(null);

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error("useAppData must be used within AppDataProvider");
  return ctx;
}

function flattenTypesWithOptions(types: any[]): any[] {
  const flattened: any[] = [];
  types?.forEach((type) => {
    flattened.push(type);
    type.options?.forEach((option: any) => {
      flattened.push(option);
    });
  });
  return flattened;
}

export function AppDataProvider({
  children,
  profileTypes,
  amenities,
  pauAmenities,
  vendorTypes,
  serviceCategories,
}: {
  children: React.ReactNode;
  profileTypes: ProfileTypes;
  amenities: any[];
  pauAmenities: any[];
  vendorTypes: any[];
  serviceCategories: any[];
}) {
  const derived = useMemo<AppDataDerived>(() => {
    const flattenedBusinessTypes = flattenTypesWithOptions(profileTypes?.BUSINESS || []);
    const flattenedEventTypes = flattenTypesWithOptions(profileTypes?.EVENT || []);

    const allTopLevelTypes = (profileTypes?.BUSINESS || [])
      .concat(profileTypes?.POI || [])
      .concat(profileTypes?.AMBASSADOR || [])
      .concat(profileTypes?.EVENT || []);
    const flattenedProfileTypes = flattenTypesWithOptions(allTopLevelTypes);

    const topLevelVendorTypes = vendorTypes?.filter((pt: any) => pt.group == null);
    const flattenedVendorTypes = flattenTypesWithOptions(topLevelVendorTypes);

    const topLevelServiceCategories = serviceCategories?.filter((pt: any) => pt.group == null);
    const flattenedServiceCategories: any[] = [];
    topLevelServiceCategories?.forEach((type: any) => {
      const typeCopy = { ...type };
      delete typeCopy.options;
      flattenedServiceCategories.push(typeCopy);
      type.options?.forEach((option: any) => {
        flattenedServiceCategories.push(option);
      });
    });

    return {
      profileTypes,
      amenities,
      pauAmenities,
      vendorTypes,
      serviceCategories,
      flattenedProfileTypes,
      flattenedBusinessTypes,
      flattenedEventTypes,
      flattenedVendorTypes,
      flattenedServiceCategories,
    };
  }, [profileTypes, amenities, pauAmenities, vendorTypes, serviceCategories]);

  return (
    <AppDataContext.Provider value={derived}>
      {children}
    </AppDataContext.Provider>
  );
}
